import React, { useState, useEffect } from 'react';
import { openDB } from 'idb';

function App() {

  const loadTacheDetails = async (tacheIds) => {
    if (!tacheIds || tacheIds.length === 0) return [];
    try {
      const response = await fetch(`/api/journee-equipe-chantier-tache-details?ids=${tacheIds.join(',')}`);
      const details = await response.json();
      console.log("Détails des tâches :", details); // Pour débogage
      return details;
    } catch (error) {
      console.error("Erreur lors du chargement des détails des tâches :", error);
      return [];
    }
  };


  // États pour les données et filtres
  const [tempsItems, setTempsItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [tachesItems, setTachesItems] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState({
    period: 'all',
    task: 'all',
    project: 'all',
    employee: 'all'
  });
  const [activeTab, setActiveTab] = useState('tachesJour'); // Onglet par défaut : "Tâches du jour"

  // Conversion heures:minutes en heures décimales
  const convertToDecimalHours = (hours, minutes) => {
    return parseFloat(hours) + (parseFloat(minutes) / 60);
  };

  // Organisation des données pour le tableau "Temps des équipiers"
  const organizeDataForTable = (items) => {
    const periods = {
      '1_chargement': { label: 'Temps de chargement', tasks: [] },
      '2_matin': { label: 'Temps de chantier matin', tasks: new Map() },
      '3_apresmidi': { label: 'Temps de chantier après-midi', tasks: new Map() },
      '4_dechargement': { label: 'Déchargement', tasks: [] }
    };
    const employees = new Map();
    items.forEach(item => {
      if (item.employee_id) {
        employees.set(item.employee_id[0], { id: item.employee_id[0], name: item.employee_id[1] });
      }
    });
    items.forEach(item => {
      const period = item.matin_ou_pm;
      const taskName = item.task_id ? item.task_id[1] : 'Tâche non définie';
      const employee = { id: item.employee_id[0], name: item.employee_id[1] };
      if (period === '1_chargement' || period === '4_dechargement') {
        if (!periods[period].tasks.some(t => t.employee.id === employee.id)) {
          periods[period].tasks.push({ employee, hours: item.temps_de_travail_hours || 0 });
        }
      } else if (period === '2_matin' || period === '3_apresmidi') {
        if (!periods[period].tasks.has(taskName)) {
          periods[period].tasks.set(taskName, []);
        }
        if (!periods[period].tasks.get(taskName).some(e => e.employee.id === employee.id)) {
          periods[period].tasks.get(taskName).push({ employee, hours: item.temps_de_travail_hours || 0 });
        }
      }
    });
    for (const period in periods) {
      if (periods[period].tasks instanceof Map) {
        periods[period].tasks = Array.from(periods[period].tasks.entries()).map(([taskName, employees]) => ({ taskName, employees }));
      }
    }
    return { periods, employees: Array.from(employees.values()) };
  };

  // Mise à jour d'une cellule
  const handleCellChange = async (period, taskName, employeeId, hours) => {
    const db = await openDB('TempsDeTravailDB', 1);
    const itemToUpdate = tempsItems.find(item =>
      item.matin_ou_pm === period &&
      (taskName ? item.task_id && item.task_id[1] === taskName : true) &&
      item.employee_id && item.employee_id[0] === employeeId
    );
    if (itemToUpdate) {
      await db.put('temps', { ...itemToUpdate, temps_de_travail_hours: hours, is_synced: false });
      const updatedItems = await db.getAll('temps');
      setTempsItems(updatedItems);
      applyFilters(updatedItems, filters);
    }
  };

  // Application des mêmes heures/minutes à toutes les entrées filtrées (avec confirmation)
  const applySameHoursToAll = async (hours) => {
    const isConfirmed = window.confirm(`Êtes-vous sûr de vouloir appliquer ${hours} heure(s) à toutes les entrées filtrées ?`);
    if (!isConfirmed) return;
    const db = await openDB('TempsDeTravailDB', 1);
    const updatedItems = [...tempsItems];
    for (const item of filteredItems) {
      const currentItem = await db.get('temps', item.id);
      const minutes = (currentItem.temps_de_travail_hours || 0) % 1 * 60;
      const newHours = hours + (minutes / 60);
      await db.put('temps', { ...currentItem, temps_de_travail_hours: newHours, is_synced: false });
      const index = updatedItems.findIndex(i => i.id === item.id);
      if (index !== -1) {
        updatedItems[index] = { ...updatedItems[index], temps_de_travail_hours: newHours };
      }
    }
    setTempsItems(updatedItems);
    applyFilters(updatedItems, filters);
  };

  const applySameMinutesToAll = async (minutes) => {
    const isConfirmed = window.confirm(`Êtes-vous sûr de vouloir appliquer ${minutes} minute(s) à toutes les entrées filtrées ?`);
    if (!isConfirmed) return;
    const db = await openDB('TempsDeTravailDB', 1);
    const updatedItems = [...tempsItems];
    for (const item of filteredItems) {
      const currentItem = await db.get('temps', item.id);
      const hours = Math.floor(currentItem.temps_de_travail_hours || 0);
      const newHours = hours + (minutes / 60);
      await db.put('temps', { ...currentItem, temps_de_travail_hours: newHours, is_synced: false });
      const index = updatedItems.findIndex(i => i.id === item.id);
      if (index !== -1) {
        updatedItems[index] = { ...updatedItems[index], temps_de_travail_hours: newHours };
      }
    }
    setTempsItems(updatedItems);
    applyFilters(updatedItems, filters);
  };

  // Chargement des données pour une date spécifique
  const loadDataForDate = async (date) => {
    const db = await openDB('TempsDeTravailDB', 1, {
      upgrade(db) { db.createObjectStore('temps', { keyPath: 'id' }); },
    });
    const dateStr = date.toISOString().split('T')[0];
    const response = await fetch(`/api/temps-de-travail?date=${dateStr}`);
    const data = await response.json();
    await db.clear('temps');
    await Promise.all(data.map(item => db.add('temps', { ...item, is_synced: false })));
    setTempsItems(data);
    applyFilters(data, filters);
  };

  // Application des filtres
  const applyFilters = (items, activeFilters) => {
    let result = [...items];
    if (activeFilters.period !== 'all') {
      result = result.filter(item => item.matin_ou_pm === activeFilters.period);
    }
    if (activeFilters.task !== 'all') {
      result = result.filter(item => item.task_id && item.task_id[0] == activeFilters.task);
    }
    if (activeFilters.project !== 'all') {
      result = result.filter(item => item.temps_de_travail_project_id && item.temps_de_travail_project_id[0] == activeFilters.project);
    }
    if (activeFilters.employee !== 'all') {
      result = result.filter(item => item.employee_id && item.employee_id[0] == activeFilters.employee);
    }
    setFilteredItems(result);
  };

  // Gestion des changements de filtre
  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);
  };

  // Récupération des options de filtre
  const getFilterOptions = (items, type) => {
    const options = new Set();
    options.add(JSON.stringify({
      id: 'all',
      name: type === 'period' ? 'Toutes périodes' :
            type === 'task' ? 'Toutes tâches' :
            type === 'project' ? 'Tous projets' : 'Tous employés'
    }));
    items.forEach(item => {
      if (type === 'period' && item.matin_ou_pm) {
        options.add(JSON.stringify({ id: item.matin_ou_pm, name: getPeriodLabel(item.matin_ou_pm) }));
      } else if (type === 'task' && item.task_id) {
        options.add(JSON.stringify({ id: item.task_id[0], name: item.task_id[1] }));
      } else if (type === 'project' && item.temps_de_travail_project_id) {
        options.add(JSON.stringify({ id: item.temps_de_travail_project_id[0], name: item.temps_de_travail_project_id[1] }));
      } else if (type === 'employee' && item.employee_id) {
        options.add(JSON.stringify({ id: item.employee_id[0], name: item.employee_id[1] }));
      }
    });
    return Array.from(options).map(opt => JSON.parse(opt));
  };

  // Traduction des périodes
  const getPeriodLabel = (period) => {
    switch (period) {
      case '1_chargement': return 'Chargement';
      case '2_matin': return 'Matin';
      case '3_apresmidi': return 'Après-midi';
      case '4_dechargement': return 'Déchargement';
      default: return period;
    }
  };

  // Formatage de la date
  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Navigation entre les dates
  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };
  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  // Rendu d'une cellule de temps
  const renderTimeCell = (period, taskName, employee, hours) => {
    return (
      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
        <input
          type="number"
          min="0"
          value={Math.floor(hours)}
          onChange={(e) => {
            const newHours = parseInt(e.target.value) || 0;
            const minutes = (hours % 1) * 60;
            handleCellChange(period, taskName, employee.id, newHours + (minutes / 60));
          }}
          style={{ width: '50px', padding: '5px' }}
        />
        h
        <input
          type="number"
          min="0"
          max="59"
          value={Math.round((hours % 1) * 60)}
          onChange={(e) => {
            const newMinutes = parseInt(e.target.value) || 0;
            const currentHours = Math.floor(hours);
            handleCellChange(period, taskName, employee.id, currentHours + (newMinutes / 60));
          }}
          style={{ width: '50px', padding: '5px' }}
        />
        min
      </div>
    );
  };

  // Chargement des journées d'équipe pour la date sélectionnée
  const loadJourneesEquipeForDate = async (date) => {
    const dateStr = date.toISOString().split('T')[0]; // Format : YYYY-MM-DD
    try {
      const responseJournees = await fetch(`/api/journee-equipe-chantier?date=${dateStr}`);
      const journees = await responseJournees.json();
  
      // Pour chaque journée, charger les détails des tâches associées
      const journeesAvecDetails = await Promise.all(
        journees.map(async (journee) => {
          const tacheDetails = await loadTacheDetails(journee.journee_equipe_chantier_tache_ids);
          return { ...journee, tacheDetails };
        })
      );
  
      setTachesItems(journeesAvecDetails);
    } catch (error) {
      console.error("Erreur lors du chargement des journées :", error);
      setTachesItems([]);
    }
  };
  

  // Synchronisation du champ finished_is
  const syncFinishedIs = async (tacheId, newValue) => {
    try {
      const response = await fetch('/api/sync-tache-finished', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tacheId, finished_is: newValue }),
      });
      if (!response.ok) throw new Error("Erreur lors de la synchronisation.");
      const updatedTaches = tachesItems.map(tache =>
        tache.id === tacheId ? { ...tache, finished_is: newValue } : tache
      );
      setTachesItems(updatedTaches);
    } catch (error) {
      console.error("Erreur :", error);
      alert(`Erreur : ${error.message}`);
    }
  };

  // Réinitialisation des filtres
  const resetFilters = () => {
    const newFilters = { period: 'all', task: 'all', project: 'all', employee: 'all' };
    setFilters(newFilters);
  };

  // Synchronisation des temps de travail
  const syncData = async () => {
    try {
      const db = await openDB('TempsDeTravailDB', 1);
      const items = await db.getAll('temps');
      for (const item of items) {
        if (!item.is_synced) {
          const response = await fetch('/api/sync-temps', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(item),
          });
          if (!response.ok) throw new Error(`Erreur lors de la synchronisation de l'entrée ${item.id}`);
          await db.put('temps', { ...item, is_synced: true });
        }
      }
      alert('Synchronisation terminée !');
      const updatedItems = await db.getAll('temps');
      setTempsItems(updatedItems);
      applyFilters(updatedItems, filters);
    } catch (error) {
      console.error("Erreur lors de la synchronisation :", error);
      alert(`Erreur lors de la synchronisation : ${error.message}`);
    }
  };

  // Création de la matrice de données pour l'affichage
  const createDataMatrix = () => {
    const { periods, employees } = organizeDataForTable(filteredItems);
    const matrix = {};
    const employeeList = employees;
    const periodsToShow = Object.keys(periods).filter(period =>
      (period === '1_chargement' || period === '4_dechargement') ?
      periods[period].tasks.length > 0 :
      periods[period].tasks.length > 0
    );
    if (periodsToShow.length === 0 && filters.period === 'all') {
      return { matrix: {}, employees: employeeList, periodsToShow: Object.keys(periods) };
    }
    periodsToShow.forEach(period => {
      if (period === '1_chargement' || period === '4_dechargement') {
        matrix[period] = { label: periods[period].label, rows: [{}], employeeData: {} };
        employeeList.forEach(emp => {
          const empData = periods[period].tasks.find(t => t.employee.id === emp.id);
          matrix[period].employeeData[emp.id] = { hours: empData ? empData.hours : 0 };
        });
      } else {
        matrix[period] = { label: periods[period].label, rows: [], employeeData: {} };
        periods[period].tasks.forEach(task => {
          const row = { taskName: task.taskName, employeeData: {} };
          employeeList.forEach(emp => {
            const empData = task.employees.find(e => e.employee.id === emp.id);
            row.employeeData[emp.id] = { hours: empData ? empData.hours : 0 };
          });
          matrix[period].rows.push(row);
        });
      }
    });
    return { matrix, employees: employeeList, periodsToShow };
  };

  // Effets de bord
  useEffect(() => {
    loadDataForDate(currentDate);
  }, [currentDate]);

  useEffect(() => {
    applyFilters(tempsItems, filters);
  }, [filters, tempsItems]);

  useEffect(() => {
    if (activeTab === 'tachesJour') {
      loadJourneesEquipeForDate(currentDate);
    }
  }, [activeTab, currentDate]);

  // Rendu
  const { matrix, employees: employeeList, periodsToShow } = createDataMatrix();

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '30px' }}>Saisie des Temps de Travail</h1>

      {/* Navigation par date */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px', gap: '20px' }}>
        <button onClick={handlePreviousDay} style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
          PREC.
        </button>
        <div style={{ fontWeight: 'bold', fontSize: '18px' }}>Date : {formatDate(currentDate)}</div>
        <button onClick={handleNextDay} style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
          SUIV.
        </button>
      </div>

      {/* Onglets */}
      <div style={{ display: 'flex', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('tachesJour')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'tachesJour' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'tachesJour' ? 'white' : 'black',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Tâches du jour
        </button>
        <button
          onClick={() => setActiveTab('tempsEquipiers')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'tempsEquipiers' ? '#007bff' : '#f0f0f0',
            color: activeTab === 'tempsEquipiers' ? 'white' : 'black',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Temps des équipiers
        </button>
      </div>

      {/* Filtres (uniquement pour l'onglet "Temps des équipiers") */}
      {activeTab === 'tempsEquipiers' && (
        <div style={{ backgroundColor: '#f5f5f5', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h2 style={{ marginTop: 0 }}>Filtres</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label htmlFor="period-filter" style={{ display: 'block', marginBottom: '5px' }}>Période</label>
              <select id="period-filter" value={filters.period} onChange={(e) => handleFilterChange('period', e.target.value)} style={{ width: '100%', padding: '8px' }}>
                {getFilterOptions(tempsItems, 'period').map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="task-filter" style={{ display: 'block', marginBottom: '5px' }}>Tâche</label>
              <select id="task-filter" value={filters.task} onChange={(e) => handleFilterChange('task', e.target.value)} style={{ width: '100%', padding: '8px' }}>
                {getFilterOptions(tempsItems, 'task').map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="project-filter" style={{ display: 'block', marginBottom: '5px' }}>Projet</label>
              <select id="project-filter" value={filters.project} onChange={(e) => handleFilterChange('project', e.target.value)} style={{ width: '100%', padding: '8px' }}>
                {getFilterOptions(tempsItems, 'project').map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="employee-filter" style={{ display: 'block', marginBottom: '5px' }}>Employé</label>
              <select id="employee-filter" value={filters.employee} onChange={(e) => handleFilterChange('employee', e.target.value)} style={{ width: '100%', padding: '8px' }}>
                {getFilterOptions(tempsItems, 'employee').map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
              </select>
            </div>
          </div>
          <button onClick={resetFilters} style={{ marginTop: '15px', padding: '8px 15px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
            Réinitialiser les filtres
          </button>
        </div>
      )}

      {/* Contenu des onglets */}
      {activeTab === 'tachesJour' && (
        <div>
          <h2>Tâches du jour</h2>
          {tachesItems.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '20px' }}>Aucune journée d'équipe trouvée pour cette date.</p>
          ) : (
            tachesItems.map((journee) => (
              <div key={journee.id} style={{ marginBottom: '30px', border: '1px solid #ddd', padding: '15px', borderRadius: '5px' }}>
                <h3>Journée : {journee.name}</h3>
                {journee.tacheDetails && journee.tacheDetails.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tâche</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Heures totales</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Heures déjà travaillées</th>
                        <th style={{ padding: '10px', border: '1px solid #ddd' }}>Terminé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journee.tacheDetails.map((tache) => (
                        <tr key={tache.id}>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            {tache.task_id[1]} {/* Nom de la tâche */}
                          </td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            {tache.total_heures_travaillees_tache}
                          </td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            {tache.total_heures_deja_travaillees_tache}
                          </td>
                          <td style={{ padding: '10px', border: '1px solid #ddd' }}>
                            <input
                              type="checkbox"
                              checked={tache.finished_is}
                              onChange={(e) => syncFinishedIs(tache.id, e.target.checked)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Aucune tâche associée.</p>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'tempsEquipiers' && (
        <div>
          <h2>Saisie des temps ({filteredItems.length} entrée{filteredItems.length > 1 ? 's' : ''})</h2>
          {filteredItems.length > 0 && (
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Heures :</p>
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(hour => (
                    <button key={`hour-${hour}`} onClick={() => applySameHoursToAll(hour)} style={{ padding: '5px 10px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                      {hour}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>Minutes :</p>
                <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(minute => (
                    <button key={`minute-${minute}`} onClick={() => applySameMinutesToAll(minute)} style={{ padding: '5px 10px', backgroundColor: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>
                      {minute}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {filteredItems.length === 0 ? (
            <p style={{ textAlign: 'center', padding: '20px' }}>Aucune donnée trouvée avec les filtres actuels.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {periodsToShow.map(period => {
                const periodData = matrix[period];
                return (
                  <div key={period} style={{ marginBottom: '30px' }}>
                    <h3 style={{ marginBottom: '15px' }}>{periodData.label}</h3>
                    {period === '1_chargement' || period === '4_dechargement' ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>{periodData.label}</th>
                            {employeeList.map(emp => <th key={emp.id} style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>{emp.name}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td style={{ padding: '10px', border: '1px solid #ddd' }}></td>
                            {employeeList.map(emp => (
                              <td key={emp.id} style={{ padding: '10px', border: '1px solid #ddd' }}>
                                {renderTimeCell(period, null, emp, periodData.employeeData[emp.id]?.hours || 0)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Tâche</th>
                            {employeeList.map(emp => <th key={emp.id} style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>{emp.name}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {periodData.rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              <td style={{ padding: '10px', border: '1px solid #ddd' }}>{row.taskName}</td>
                              {employeeList.map(emp => (
                                <td key={emp.id} style={{ padding: '10px', border: '1px solid #ddd' }}>
                                  {renderTimeCell(period, row.taskName, emp, row.employeeData[emp.id]?.hours || 0)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ textAlign: 'center' }}>
            <button onClick={syncData} style={{ padding: '12px 25px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '5px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' }}>
              SYNCHRONISER LES DONNÉES
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
